// PostHog product analytics — funnels, retention, session replay, autocapture,
// $initial_utm_* attribution.
//
// Trimmed for the marketing site: only `initPosthog` and `capturePosthogEvent`
// remain. The identify/reset helpers were paired with authService (deleted on
// www — no auth today). When a future consumer-auth story lands here, port
// `identifyPosthogProfile` + `resetPosthog` back from core.
//
// Gated by `VITE_POSTHOG_KEY`. Missing key → every helper is a silent no-op,
// so dev builds default to "off" until the developer sets the key locally.
// Set `VITE_POSTHOG_KEY` (+ optional `VITE_POSTHOG_HOST`, defaults to PostHog
// US cloud) in `.env.{dev,stg,prod,local}` to enable.
//
// All helpers wrap calls in try/catch — PostHog failing must never break the
// app. The PostHog SDK itself is also resilient (queues events offline,
// retries), so most errors here would be malformed input on our side.

import posthog from 'posthog-js';
import { logDebug, logError } from './loggerService';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

let isInitialized = false;

/**
 * Bootstrap PostHog. Call once early in app startup. `appName` is registered
 * as a super-property `app` so every event from every app in the suite is
 * filterable by which app fired it — funnels across `www` (landing) →
 * `core` (signup) work via this property.
 *
 * No-op when `VITE_POSTHOG_KEY` is unset. Idempotent — second call short-
 * circuits.
 */
export function initPosthog(appName: 'core' | 'www' | 'admin' | 'chef'): void {
  if (isInitialized) return;
  if (!POSTHOG_KEY) {
    logDebug('PostHog disabled (VITE_POSTHOG_KEY not set)');
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // SPA pageviews — fires on History API route changes so internal
      // navigation actually tracks (the default `'config'` only captures
      // the first load and would leave every funnel after the landing
      // screen empty).
      capture_pageview: 'history_change',
      // Mask all text/inputs in session replay by default — recipes,
      // addresses, names, prices can all be PII-adjacent. We can opt-in
      // specific fields later via the `ph-no-mask` CSS class if needed.
      session_recording: { maskAllInputs: true, maskTextSelector: '*' },
      // Person profiles only for identified users — anonymous pageviews
      // don't create person rows, saves quota and keeps the people view
      // focused on signed-in users.
      person_profiles: 'identified_only',
      // Cross-subdomain cookie so the anonymous distinct_id carries from
      // www.homeplated.com → app.homeplated.com → chef.homeplated.com.
      // Without this each subdomain mints its own anonymous id and the
      // UTM → marketing → signup funnel breaks at the handoff. Admin is
      // intentionally excluded from this PostHog project (internal-only).
      cross_subdomain_cookie: true,
    });
    // Stamped on every event so cross-app queries can filter / group by
    // origin app without joining anything.
    posthog.register({ app: appName });
    isInitialized = true;
    logDebug('PostHog initialized', undefined, { appName });
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'PostHog init failed');
  }
}

/**
 * Explicit event capture — use for activation-critical events that
 * autocapture might miss or render with vague selectors. Examples:
 * `contact_form_submitted`, `pricing_link_clicked`. Anonymous-safe —
 * fires even before identify, gets attributed to the user once they
 * sign in on another subdomain (via cross-subdomain cookie).
 */
export function capturePosthogEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!isInitialized) return;

  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'PostHog capture failed', undefined, { eventName });
  }
}
